import * as React from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import * as S from "@styled";

import _ from "lodash";
import * as semver from "semver";

import { AgentGroup, AgentVersionInfo } from "../../../shared/model/AgentGroup";
import { HashMap } from "../../../shared/model/HashMap";
import { Process } from "../../model/Process";
import { ProcessVersionDetails } from "../../model/ProcessVersion";
import { State } from "../../model/State";

import * as Actions from "../../actions/DeployActions";

import { DispatchType } from "../../../shared/utilities";
import { DeployDialogBody } from "./DeployDialogBody";
import { DeployDialogFooter } from "./DeployDialogFooter";
import { DeployDialogHeader } from "./DeployDialogHeader";
import { DeploymentInformationProps } from "../DeploymentInformation";
import { DeploymentInformationIcon } from "../DeploymentInformationIcon";
import { ChangeEvent } from "../../../shared/model/Event";
import CustomModal from "shared/components/CustomModal";
import { Checkbox } from "shared/components/Checkbox";
import { VersionSelectorDropdown } from "../VersionSelectorDropdown";
import { IProcessVariable } from "../../../processEditor/model/IProcessData";
import { DeployDialogProcessVariableDisplay } from "./DeployDialogProcessVariableDisplay";

const StyledProcessNameColumnWrapper = styled.div`
    padding-right: 16px;

    p {
        margin: 0;
        overflow: hidden;
        text-overflow: ellipsis;

        + div {
            margin-top: 14px;
        }
    }
`;

const StyledExcludeColumnWrapper = styled.div`
    display: flex;
    justify-content: center;
`;

export const DeployDialog: React.FC<DeployDialogProps> = (props) => {
    const [deploymentDescription, setDeploymentDescription] = React.useState("");

    if (!props.showDeployDialog) {
        return null;
    }

    if (props.selectedProcesses.length <= 0) {
        return <div>{FRENDS.Localizations.Warning_NoProcesses}</div>;
    }

    const anySubprocesses: boolean = props.selectedProcesses
        .map((selectedProcess: Process) => props.processVersionsPerGuid[selectedProcess.uniqueIdentifier]
            .find((pvd: ProcessVersionDetails) => pvd.version === props.versionsToDeploy[selectedProcess.uniqueIdentifier]))
        .some((pvd: ProcessVersionDetails) => pvd.usedSubprocesses.length);

    const deploymentInformationIconColumn: Column = { id: "deploymentInformationIconColumn", width: "15px", name: "", shown: true };
    const processVariablesColumn: Column = { id: "processVariables", width: "1fr", name: "Process Variables", shown: !!props.suggestedProcessVariablesForDeployment };
    const nameColumn: Column = { id: "name", width: "1fr", name: FRENDS.Localizations.Process, shown: true };
    const versionColumn: Column = { id: "version", width: "minmax(100px, max-content)", name: FRENDS.Localizations.Version, shown: true };
    const excludeColumn: Column = { id: "exclude", width: "auto", name: "Exclude", shown: (props.isApiDeployment && props.selectedProcesses.length > 1) };
    const subprocessColumn: Column = { id: "subprocesses", width: "auto", name: FRENDS.Localizations.Subprocesses, shown: anySubprocesses };

    const visibleColumns = [
        deploymentInformationIconColumn,
        nameColumn,
        processVariablesColumn,
        versionColumn,
        excludeColumn,
        subprocessColumn,
    ].filter((column: Column) => column.shown);

    let incompatibleAgent = false;

    const table: DeployDialogTable = {
        columns: visibleColumns,
        cells:
            props.selectedProcesses.map((process: Process) => {
                let versionsForProcess = props.processVersionsPerGuid[process.uniqueIdentifier];
                if (!versionsForProcess) {
                    console.error(`No versions found for process ${process.name}. Will just show the current version`);
                    versionsForProcess = [{
                        processId: process.id,
                        processGuid: process.uniqueIdentifier,
                        version: process.version,
                        majorVersion: process.majorVersion,
                        minorVersion: process.minorVersion,
                        frendsVersion: "",
                        processName: process.name,
                        changeDescription: "",
                        deployedToAgentGroups: [],
                        usedSubprocesses: [],
                        isCrossPlatform: process.isCrossPlatform,
                        isDotNet: process.isDotNet
                    }];
                }
                let selectedVersion = props.versionsToDeploy[process.uniqueIdentifier];
                if (!selectedVersion) {
                    console.debug(`No selected deploy version yet for process ${process.name}. Will use the process one by default.`);
                    selectedVersion = process.version;
                }
                // This should never be undefined as it will use the defaults from above if the version was not found.
                const selectedProcessVersion = versionsForProcess.find((v) => v.version === selectedVersion);
                let downgradeWarning: JSX.Element | null = null;
                if (shouldShowDowngradeWarning(selectedVersion, versionsForProcess, props.targetAgentGroupName)) {
                    downgradeWarning = (
                        <S.WarningContainer>
                            <div className="alert-warning">
                                <div className="alert-label">
                                    {FRENDS.Localizations.Warning}
                                </div>
                                <div className="alert-content">
                                    {FRENDS.Localizations.Process_NewerVersionDeployed} <b>{props.targetAgentGroupName}</b>.</div>
                            </div>
                        </S.WarningContainer>
                    );
                }
                let incompatibleAgentWarning: JSX.Element | null = null;
                const [incorrectTarget, isDotNetTarget] = processTargetsIncorrectVersion(selectedProcessVersion, props.targetAgentGroupName, props.agentGroups);
                if (incorrectTarget) {
                    incompatibleAgent = true;
                    incompatibleAgentWarning = (
                        <S.WarningContainer>
                            <div className="alert-warning">
                                <div className="alert-label">
                                    {FRENDS.Localizations.Warning}
                                </div>
                                <div className="alert-content">
                                    {isDotNetTarget ? FRENDS.Localizations.Process_WrongTargetFrameworkDotNet : FRENDS.Localizations.Process_WrongTargetFramework}
                                    <b> {props.targetAgentGroupName}</b>.
                                </div>
                            </div>
                        </S.WarningContainer>
                    );
                } else if (processVersionIncompatibleWithAgents(selectedVersion, versionsForProcess, props.targetAgentGroupName, props.agentVersions)) {
                    incompatibleAgent = true;
                    incompatibleAgentWarning = (
                        <S.WarningContainer>
                            <div className="alert-warning">
                                <div className="alert-label">
                                    {FRENDS.Localizations.Warning}
                                </div>
                                <div className="alert-content">
                                    {FRENDS.Localizations.Process_AgentIncompatible} <b>{props.targetAgentGroupName}</b>.
                                </div>
                            </div>
                        </S.WarningContainer>
                    );
                }

                const processNameColumn = <StyledProcessNameColumnWrapper>
                    <p>{selectedProcessVersion?.processName}</p>
                    {downgradeWarning}
                    {incompatibleAgentWarning}
                </StyledProcessNameColumnWrapper>;

                const processVariables = (selectedProcessVersion == null || props.suggestedProcessVariablesForDeployment == null)
                    ? <></>
                    : <ul>{props.suggestedProcessVariablesForDeployment[selectedProcessVersion?.processId]?.map(p => <li key={p.name}><DeployDialogProcessVariableDisplay processVariable={p} /></li>)}</ul>

                const versionSelect = <VersionSelectorDropdown
                    agentGroups={props.agentGroups}
                    getVersionString={getVersionString}
                    onSelectVersion={props.setVersionToDeploy}
                    processGuid={process.uniqueIdentifier}
                    selectedVersion={selectedVersion}
                    versionRows={versionsForProcess}
                />

                const excludeProcessCheckbox = <StyledExcludeColumnWrapper>
                    <Checkbox className="exclude-process-selector" onChange={(event) => props.onToggleProcessExclusion(event.target.checked, process.uniqueIdentifier)} />
                </StyledExcludeColumnWrapper>;

                return [
                    { columnId: deploymentInformationIconColumn.id, value: getDeploymentInformationIcon(versionsForProcess, props.targetAgentGroupName, props.isSwitchVersion) },
                    { columnId: nameColumn.id, value: processNameColumn },
                    { columnId: processVariablesColumn.id, value: processVariables },
                    { columnId: versionColumn.id, value: versionSelect },
                    { columnId: excludeColumn.id, value: excludeProcessCheckbox },
                    { columnId: subprocessColumn.id, value: <>{selectedProcessVersion?.usedSubprocesses.join(", ")}</> },
                ].filter((cell) => visibleColumns.some((visibleColumn) => visibleColumn.id === cell.columnId));
            })
    };

    const onChangeDeploymentDescription = (event: ChangeEvent<HTMLTextAreaElement>) => setDeploymentDescription(event.target.value);

    return (
        <CustomModal
            label="deploy-modal"
            onClose={props.closeDeployDialog}
            size={"medium"}
            show={true}
            shouldCloseOnOverlayClick={true}
            modalTitle={<DeployDialogHeader
                isSwitchVersion={props.isSwitchVersion}
                selectedProcesses={props.selectedProcesses}
                hasApiPassthroughConfiguration={props.hasApiPassthroughConfiguration}
            />}
        >
            <DeployDialogBody
                agentGroupsForDeployment={props.agentGroupsForDeployment}
                isSwitchVersion={props.isSwitchVersion}
                onSetTargetAgentGroup={props.onSetTargetAgentGroup}
                table={table}
                targetAgentGroupName={props.targetAgentGroupName}
                deploymentDescription={deploymentDescription}
                onChangeDeploymentDescription={onChangeDeploymentDescription}
                warnApiPassthroughConfiguration={props.warnApiPassthroughConfiguration}
            />
            <DeployDialogFooter
                closeDeployDialog={props.closeDeployDialog}
                isSubprocessList={props.isSubprocessList}
                isSwitchVersion={props.isSwitchVersion}
                deploymentDisabled={incompatibleAgent || props.allExcluded}
                handleDeploy={props.onDeploy}
                deploymentDescription={deploymentDescription}
            />
        </CustomModal>
    );
};

const getDeploymentInformationIcon = (versionsForProcess: ProcessVersionDetails[], targetAgentGroupName: string, isSwitchVersion: boolean) => {
    if (isSwitchVersion) {
        return <></>;
    }

    const deployedProcessVersion: ProcessVersionDetails | undefined = versionsForProcess
        .find((processVersionDetails: ProcessVersionDetails) => processVersionDetails.deployedToAgentGroups
            .includes(targetAgentGroupName));

    if (!deployedProcessVersion) {
        return <></>;
    }

    const deploymentInformationProps: DeploymentInformationProps = {
        processGuid: deployedProcessVersion.processGuid,
        agentGroup: targetAgentGroupName,
        versionString: getVersionString(deployedProcessVersion),
    };

    return <DeploymentInformationIcon
        deploymentInformationProps={deploymentInformationProps}
        show={true}
        title={FRENDS.Localizations.CurrentDeployment}
    />;
};

const getVersionString = (processVersionDetails: ProcessVersionDetails) => {
    return `${processVersionDetails.majorVersion}.${processVersionDetails.minorVersion}.${processVersionDetails.version}`;
};

function processTargetsIncorrectVersion(selectedProcessVersion: ProcessVersionDetails | undefined, targetAgentGroupName: string, agentGroups: AgentGroup[]): [boolean, boolean] {
    const targetAgentGroup = agentGroups.find((ag) => ag.name.toLowerCase() === targetAgentGroupName.toLowerCase());
    const isDotNetProcess = !!(selectedProcessVersion?.isCrossPlatform && selectedProcessVersion?.isDotNet);

    // Cross-platform (ie. .Net Standard) is always valid unless it's dotnet.
    let isValid = selectedProcessVersion?.isCrossPlatform && !selectedProcessVersion?.isDotNet || targetAgentGroupName === Actions.DEFAULT_AGENT_GROUP_NAME;
    if (!isValid) {
        const isLegacyProcess = !selectedProcessVersion?.isCrossPlatform && !selectedProcessVersion?.isDotNet;

        if (targetAgentGroup?.isCrossPlatform) {
            isValid = !isLegacyProcess && isDotNetProcess;
        } else {
            isValid = isLegacyProcess && !isDotNetProcess;
        }
    }

    return [!isValid, isDotNetProcess];
}

export function processVersionIncompatibleWithAgents(
    selectedVersion: number,
    versionsForProcess: ProcessVersionDetails[],
    targetAgentGroup: string,
    agentVersions: AgentVersionInfo[],
) {
    const processVersion = versionsForProcess.find((v) => v.version === selectedVersion);
    if (!processVersion || !processVersion.frendsVersion) {
        return false; // cannot say
    }

    const processFrendsVersionSemVer = semver.coerce(processVersion.frendsVersion);
    const agentVersionsForGroup = agentVersions.filter((v) => v.agentGroupName === targetAgentGroup && v.version);

    const agentWithOldVersionIndex = agentVersionsForGroup.findIndex((agentVersion) => {
        const agentVersionSemVer = semver.coerce(agentVersion.version);
        if (agentVersionSemVer !== null) {
            return semver.lt(
                agentVersionSemVer,
                `${processFrendsVersionSemVer?.major}.${processFrendsVersionSemVer?.minor}.0`);
        }
    });

    return agentWithOldVersionIndex !== -1;
}

function shouldShowDowngradeWarning(selectedVersion: number, versionsForProcess: ProcessVersionDetails[], targetAgentGroup: string): boolean {
    const currentlyDeployedProcessVersionForAgentGroup = versionsForProcess.find((ev) => ev.deployedToAgentGroups.some((groupName) => groupName === targetAgentGroup));
    if (!currentlyDeployedProcessVersionForAgentGroup) {
        return false;
    }
    return currentlyDeployedProcessVersionForAgentGroup.version > selectedVersion;
}

export interface DeployDialogTable {
    columns: Column[];
    cells: Cell[][];
}

interface Column {
    id: string;
    width: string;
    name: string;
    shown: boolean;
}

interface Cell {
    columnId: string;
    value: JSX.Element;
    className?: string;
}

interface DeployDialogOwnProps { }

interface DeployDialogStateProps {
    showDeployDialog: boolean;
    isSwitchVersion: boolean;
    selectedProcesses: Process[];
    versionsToDeploy: HashMap<number>;
    processVersionsPerGuid: HashMap<ProcessVersionDetails[]>;
    targetAgentGroupName: string;
    agentGroupsForDeployment: AgentGroup[];
    agentGroups: AgentGroup[];
    agentVersions: AgentVersionInfo[];
    suggestedProcessVariablesForDeployment: HashMap<IProcessVariable[]> | undefined;
    isSubprocessList: boolean;
    isApiDeployment: boolean;
    allExcluded: boolean;
    warnApiPassthroughConfiguration: string | null;
    hasApiPassthroughConfiguration: boolean;
}
interface DeployDialogDispatchProps {
    setVersionToDeploy?: (processGuid: string, version: number) => void;
    onSetTargetAgentGroup: (environment: string, agentGroupName: string) => void;
    onDeploy: (activateTriggers: boolean, deploymentDescription: string) => void;
    closeDeployDialog: () => void;
    onToggleProcessExclusion: (exclude: boolean, processGuid: string) => void
}
export type DeployDialogProps = DeployDialogOwnProps & DeployDialogStateProps & DeployDialogDispatchProps;

const mapStateToProps = (state: State): DeployDialogStateProps => {
    return {
        showDeployDialog: state.deployDialogState.showDeployDialog,
        isSwitchVersion: state.deployDialogState.isSwitchVersion,
        selectedProcesses: state.deployDialogState.selectedProcesses || [],
        versionsToDeploy: state.deployDialogState.versionsToDeploy,
        processVersionsPerGuid: state.deployDialogState.processVersions,
        targetAgentGroupName: state.deployDialogState.targetAgentGroupName,
        agentGroupsForDeployment: state.deployDialogState.availableAgentGroups,
        agentVersions: state.deployDialogState.agentVersions,
        suggestedProcessVariablesForDeployment: state.deployDialogState.suggestedProcessVariablesForDeployment,
        warnApiPassthroughConfiguration: state.deployDialogState.warnApiPassthroughConfiguration,
        hasApiPassthroughConfiguration: state.deployDialogState.hasApiPassthroughConfiguration,
        agentGroups: _.values(state.agentGroups),
        isSubprocessList: state.isSubprocessList,
        isApiDeployment: false,
        allExcluded: false,
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>): DeployDialogDispatchProps => {
    return {
        closeDeployDialog: () => dispatch(Actions.closeDeployDialog()),
        onSetTargetAgentGroup: (environment: string, agentGroupName: string) => dispatch(Actions.setTargetAgentGroup(environment, agentGroupName)),
        setVersionToDeploy: (processGuid: string, version: number) => dispatch(Actions.setVersionToDeploy(processGuid, version)),
        onDeploy: (activateTriggers: boolean, deploymentDescription: string) => dispatch(Actions.deploy(activateTriggers, deploymentDescription)),
        onToggleProcessExclusion: (exclude: boolean, processGuid: string) => { },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(DeployDialog);
